import { useState } from "react";
import type { ContextEntry } from "@/api/prompts";

/** Session context ledger editor — the Context tab. Typed key-value pairs
 *  (colors get a picker) that are injected verbatim into every generation
 *  prompt. Entries arrive from three writers: the user (authoritative),
 *  the generating model, and the server's brand extractor ("site"). */

const inputClasses =
  "w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink transition-colors focus:border-line-strong focus:outline-none";

const ENTRY_TYPES: ContextEntry["type"][] = [
  "color",
  "text",
  "font",
  "url",
  "number",
  "file",
];

const SOURCE_LABEL: Record<ContextEntry["source"], string> = {
  user: "you",
  model: "model",
  site: "site",
};

const SOURCE_CLASSES: Record<ContextEntry["source"], string> = {
  user: "bg-accent-soft text-accent",
  model: "bg-line text-ink-2",
  site: "bg-line text-ink-2",
};

interface ContextPanelProps {
  entries: ContextEntry[];
  onChange: (entries: ContextEntry[]) => void;
  disabled?: boolean;
}

export function ContextPanel({ entries, onChange, disabled }: ContextPanelProps) {
  const [newKey, setNewKey] = useState("");
  const [newType, setNewType] = useState<ContextEntry["type"]>("color");
  const [newValue, setNewValue] = useState("");

  const update = (idx: number, patch: Partial<ContextEntry>) => {
    // Any hand edit makes the entry the user's — the backend treats
    // user-sourced values as authoritative and never lets the model or
    // the brand extractor overwrite them.
    onChange(
      entries.map((e, i) =>
        i === idx ? { ...e, ...patch, source: "user" } : e,
      ),
    );
  };

  const remove = (idx: number) => {
    onChange(entries.filter((_, i) => i !== idx));
  };

  const add = () => {
    const key = newKey.trim();
    const value = newValue.trim();
    if (!key || !value) return;
    if (entries.some((e) => e.key === key)) return;
    onChange([...entries, { key, type: newType, value, source: "user" }]);
    setNewKey("");
    setNewValue("");
  };

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold text-ink">Session context</h3>
        <p className="text-[11px] text-ink-3">
          Durable facts for this project — brand colors, fonts, style rules,
          attached files. Every generation sees these as exact, authoritative
          values (colors are used verbatim, never approximated). Mention a
          website in your prompt and its real palette is extracted here
          automatically; the model also records what it uses. Entries marked
          "you" always win.
        </p>

        {entries.length === 0 && (
          <p className="rounded-lg border border-dashed border-line px-3 py-4 text-center text-xs text-ink-3">
            No context yet. Add brand colors or fonts below, attach files, or
            just reference a website in your prompt.
          </p>
        )}

        <div className="flex flex-col gap-2">
          {entries.map((entry, idx) => (
            <div key={`${entry.key}-${idx}`} className="flex items-center gap-2">
              <input
                type="text"
                value={entry.key}
                onChange={(e) => update(idx, { key: e.target.value })}
                disabled={disabled}
                className={`${inputClasses} max-w-[220px] font-mono text-xs`}
                placeholder="key"
              />
              <select
                value={entry.type}
                onChange={(e) =>
                  update(idx, { type: e.target.value as ContextEntry["type"] })
                }
                disabled={disabled}
                className="rounded-lg border border-line bg-surface px-2 py-2 text-xs text-ink-2 focus:border-line-strong focus:outline-none"
              >
                {ENTRY_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              {entry.type === "color" ? (
                <div className="flex flex-1 items-center gap-2">
                  <input
                    type="color"
                    value={
                      /^#[0-9a-fA-F]{6}$/.test(entry.value)
                        ? entry.value
                        : "#000000"
                    }
                    onChange={(e) => update(idx, { value: e.target.value })}
                    disabled={disabled}
                    className="h-9 w-9 shrink-0 cursor-pointer rounded border border-line bg-transparent"
                  />
                  <input
                    type="text"
                    value={entry.value}
                    onChange={(e) => update(idx, { value: e.target.value })}
                    disabled={disabled}
                    className={`${inputClasses} font-mono text-xs`}
                    placeholder="#000000"
                  />
                </div>
              ) : (
                <input
                  type={entry.type === "number" ? "number" : "text"}
                  value={entry.value}
                  onChange={(e) => update(idx, { value: e.target.value })}
                  disabled={disabled}
                  className={`${inputClasses} flex-1`}
                  placeholder="value"
                />
              )}
              <span
                className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${SOURCE_CLASSES[entry.source] ?? SOURCE_CLASSES.model}`}
                title={
                  entry.source === "user"
                    ? "Set by you — authoritative, never overwritten"
                    : entry.source === "site"
                      ? "Extracted from a website you referenced"
                      : "Recorded by the generating model"
                }
              >
                {SOURCE_LABEL[entry.source] ?? entry.source}
              </span>
              <button
                type="button"
                onClick={() => remove(idx)}
                disabled={disabled}
                className="shrink-0 rounded p-1 text-ink-3 transition-colors hover:text-ink"
                title="Remove entry"
                aria-label={`Remove ${entry.key}`}
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2 border-t border-line pt-3">
          <input
            type="text"
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
            disabled={disabled}
            className={`${inputClasses} max-w-[220px] font-mono text-xs`}
            placeholder="brand.primary"
          />
          <select
            value={newType}
            onChange={(e) => setNewType(e.target.value as ContextEntry["type"])}
            disabled={disabled}
            className="rounded-lg border border-line bg-surface px-2 py-2 text-xs text-ink-2 focus:border-line-strong focus:outline-none"
          >
            {ENTRY_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          {newType === "color" ? (
            <div className="flex flex-1 items-center gap-2">
              <input
                type="color"
                value={/^#[0-9a-fA-F]{6}$/.test(newValue) ? newValue : "#000000"}
                onChange={(e) => setNewValue(e.target.value)}
                disabled={disabled}
                className="h-9 w-9 shrink-0 cursor-pointer rounded border border-line bg-transparent"
              />
              <input
                type="text"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && add()}
                disabled={disabled}
                className={`${inputClasses} font-mono text-xs`}
                placeholder="#000000"
              />
            </div>
          ) : (
            <input
              type={newType === "number" ? "number" : "text"}
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
              disabled={disabled}
              className={`${inputClasses} flex-1`}
              placeholder="value"
            />
          )}
          <button
            type="button"
            onClick={add}
            disabled={disabled || !newKey.trim() || !newValue.trim()}
            className="shrink-0 rounded-lg border border-line px-3 py-2 text-xs font-medium text-ink transition-colors hover:border-line-strong disabled:opacity-40"
          >
            Add
          </button>
        </div>
      </section>
    </div>
  );
}
